import type { AdClickIds } from "@tripcart/shared";
// Subpath on purpose: the zod-free half of the contract (no zod in the browser).
import {
  ADS_CONSENT_COOKIE,
  ADS_CONSENT_REQUIRED_GLOBAL,
} from "@tripcart/shared/apps/ads-storefront";

/**
 * The ad click a traveler landed from (Meta Ads / Google Ads, AC-13). Read
 * from the landing link once, kept in this browser for the 28 days Meta and
 * Google attribute a booking to a click, and sent with the checkout and the
 * inquiry form. Only mounted on storefronts with an ads app installed.
 */

const STORAGE_KEY = "tc_ad_click";
const KEEP_MS = 28 * 24 * 60 * 60 * 1000;

const PARAMS = {
  gclid: "gclid",
  fbclid: "fbclid",
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmContent: "utm_content",
} as const;

type Landed = Partial<Record<keyof typeof PARAMS, string>> & { at: number };

const clip = (v: string | null, max: number): string | undefined =>
  v ? v.trim().slice(0, max) || undefined : undefined;

/** Saves the landing link's click ids. A new ad click replaces the old one;
 *  a page with none leaves it alone. */
export function captureAdClick(search: string, now = Date.now()): void {
  const q = new URLSearchParams(search);
  const landed: Landed = { at: now };
  let found = false;
  for (const [key, param] of Object.entries(PARAMS)) {
    const value = clip(q.get(param), key === "fbclid" ? 500 : 200);
    if (value) {
      landed[key as keyof typeof PARAMS] = value;
      found = true;
    }
  }
  if (!found) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(landed));
  } catch {
    // Storage blocked: the booking just carries no click id.
  }
}

function readLanded(now: number): Landed | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const landed = raw ? (JSON.parse(raw) as Landed) : null;
    return landed && now - landed.at < KEEP_MS ? landed : null;
  } catch {
    return null;
  }
}

/** Runs on every checkout, so a cookie another script wrote badly must not
 *  throw: `decodeURIComponent` does on a stray `%`. */
function cookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
}

/** Same rule the ads tags follow (`ads-scripts.ts`): consent counts as given
 *  unless Cookie Consent asks for it and the visitor has not accepted. */
function hasAdsConsent(): boolean {
  const asked =
    (window as unknown as Record<string, unknown>)[
      ADS_CONSENT_REQUIRED_GLOBAL
    ] === true;
  return !asked || cookie(ADS_CONSENT_COOKIE) === "granted";
}

/** The ids to send with a checkout or inquiry; undefined when there are none. */
export function readAdClickIds(now = Date.now()): AdClickIds | undefined {
  const landed = readLanded(now);
  const consent = hasAdsConsent();
  // Meta's cookies are only read once the visitor allowed ads cookies.
  const fbp = consent ? clip(cookie("_fbp") ?? null, 200) : undefined;
  const fbc = consent
    ? clip(
        cookie("_fbc") ??
          (landed?.fbclid ? `fb.1.${landed.at}.${landed.fbclid}` : null),
        500,
      )
    : undefined;
  const ids: Partial<Landed> = { ...landed };
  delete ids.at;
  const out: AdClickIds = { ...ids, fbc, fbp, adsConsent: consent };
  const hasAny = Object.entries(out).some(
    ([k, v]) => k !== "adsConsent" && v !== undefined,
  );
  return hasAny ? out : undefined;
}
